import React, { useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useTranslation } from "react-i18next";
import { createCustomerPanelUi, CUSTOMER_PANEL_ACCENTS } from "./customerPanelStyles";

const fieldGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  columnGap: 2,
  rowGap: 2.75,
  alignItems: "start",
};

const fieldStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2.75,
};

const CustomerDetails = ({
  customer,
  marcas,
  handleCustomerChange,
  handleMultiSelectChange,
  customerId,
  canUpdate,
  saving = false,
  COLUMN_WIDTH,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const accent = CUSTOMER_PANEL_ACCENTS.client;
  const ui = createCustomerPanelUi(theme, accent);
  const [expandedSection, setExpandedSection] = useState({
    commercial: true,
    company: true,
    business: false,
  });

  const panelSx = {
    ...ui.panelSx,
    width: COLUMN_WIDTH,
    minWidth: COLUMN_WIDTH,
    maxWidth: COLUMN_WIDTH,
  };
  const bodySx = {
    ...ui.bodySx,
    ...ui.fieldSx,
    px: 1.75,
    py: 1.5,
    "& .MuiFormControl-root": {
      margin: 0,
    },
    "& .MuiTextField-root": {
      margin: 0,
    },
    "& .MuiInputLabel-root": {
      lineHeight: 1.25,
    },
    "& .MuiInputBase-root": {
      minHeight: 40,
    },
    "& .MuiSelect-select": {
      minHeight: "1.25rem",
      display: "flex",
      alignItems: "center",
    },
  };
  const companyName = customer.name || "—";

  const accordionSx = {
    mb: 1.35,
    border: "none",
    borderRadius: 2.5,
    overflow: "visible",
    bgcolor: alpha(theme.palette.background.paper, 0.82),
    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`,
    "&:before": { display: "none" },
    "&.Mui-expanded": { mb: 1.35 },
  };

  const accordionSummarySx = {
    minHeight: 48,
    px: 1.5,
    "& .MuiAccordionSummary-content": {
      my: 0.75,
      alignItems: "center",
      gap: 1,
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
      color: "text.secondary",
    },
  };

  const accordionDetailsSx = {
    px: 1.5,
    pt: 0.5,
    pb: 1.75,
  };

  const toggleSection = (key) => (_, isExpanded) => {
    setExpandedSection((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const renderMultiChips = (selected) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {selected.map((value) => (
        <Chip key={value} label={value} size="small" />
      ))}
    </Box>
  );

  return (
    <Box sx={panelSx}>
      <Box sx={{ ...ui.headerSx, pl: 2, flexDirection: "column", alignItems: "stretch" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={ui.headerEyebrowSx}>
              {t("Clientes.Client")}
            </Typography>
            <Typography sx={ui.headerTitleSx}>{companyName}</Typography>
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!canUpdate || saving}
            title={
              !canUpdate
                ? t("Clientes.noUpdatePermission", {
                    defaultValue:
                      "No tienes permiso para crear/actualizar clientes (Permiso_ClientsUpdate).",
                  })
                : undefined
            }
            sx={{ ...ui.saveButtonSx, flexShrink: 0 }}
          >
            {saving
              ? t("Clientes.saving", { defaultValue: "Guardando…" })
              : customerId
                ? t("Clientes.Actualizar")
                : t("Clientes.Add")}
          </Button>
        </Box>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1, pl: 0.15, pb: 0.25 }}>
          {customer.estado ? (
            <Chip
              size="small"
              label={t(`Clientes.${customer.estado}`, { defaultValue: customer.estado })}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.68rem",
                bgcolor: alpha(accent, 0.12),
                color: theme.palette.primary.dark,
              }}
            />
          ) : null}
          {customer.abc ? (
            <Chip
              size="small"
              variant="outlined"
              label={`ABC ${customer.abc}`}
              sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
            />
          ) : null}
          {customer.createdAt ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${t("Clientes.Created")} ${customer.createdAt}`}
              sx={{ height: 22, fontSize: "0.66rem", fontWeight: 600 }}
            />
          ) : null}
          {customer.updatedAt ? (
            <Chip
              size="small"
              variant="outlined"
              label={`${t("Clientes.UpdatedDate")} ${customer.updatedAt}`}
              sx={{ height: 22, fontSize: "0.66rem", fontWeight: 600 }}
            />
          ) : null}
        </Stack>
      </Box>

      <Box sx={bodySx}>
        <Accordion
          expanded={expandedSection.commercial}
          onChange={toggleSection("commercial")}
          disableGutters
          elevation={0}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography sx={ui.sectionTitleSx}>
              {t("Clientes.commercialSection", { defaultValue: "Comercial" })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <Box sx={fieldGridSx}>
              <FormControl fullWidth size="small">
                <InputLabel id="estado-label">{t("Clientes.Lead")}</InputLabel>
                <Select
                  labelId="estado-label"
                  name="estado"
                  value={customer.estado}
                  label={t("Clientes.Lead")}
                  onChange={handleCustomerChange}
                >
                  <MenuItem value="New">{t("Clientes.New")}</MenuItem>
                  <MenuItem value="Ok Financiero">{t("Clientes.Ok Financiero")}</MenuItem>
                  <MenuItem value="Estudio Cliente">{t("Clientes.Estudio Cliente")}</MenuItem>
                  <MenuItem value="Contactando">{t("Clientes.Contactando")}</MenuItem>
                  <MenuItem value="Pain Presentación">{t("Clientes.Pain Presentación")}</MenuItem>
                  <MenuItem value="Muestra Interés">{t("Clientes.Muestra Interés")}</MenuItem>
                  <MenuItem value="En el Ring">{t("Clientes.En el Ring")}</MenuItem>
                  <MenuItem value="Esperando decisión">{t("Clientes.Esperando decisión")}</MenuItem>
                  <MenuItem value="Ganado">{t("Clientes.Ganado")}</MenuItem>
                  <MenuItem value="Perdido">{t("Clientes.Perdido")}</MenuItem>
                  <MenuItem value="Inactivo">{t("Clientes.Inactivo")}</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel id="procedencia-label">{t("Clientes.typeGarment")}</InputLabel>
                <Select
                  labelId="procedencia-label"
                  name="procedencia"
                  multiple
                  label={t("Clientes.typeGarment")}
                  value={customer.procedencia}
                  onChange={(e) => handleMultiSelectChange(e, "procedencia")}
                  renderValue={renderMultiChips}
                >
                  <MenuItem value="Outerwear">Outerwear</MenuItem>
                  <MenuItem value="Light Woven">Light Woven</MenuItem>
                  <MenuItem value="Knitted">Knitted</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel id="tipo_envios-label">{t("Clientes.Origin")}</InputLabel>
                <Select
                  labelId="tipo_envios-label"
                  name="tipo_envios"
                  multiple
                  label={t("Clientes.Origin")}
                  value={customer.tipo_envios}
                  onChange={(e) => handleMultiSelectChange(e, "tipo_envios")}
                  renderValue={renderMultiChips}
                >
                  <MenuItem value="Stage">Stage</MenuItem>
                  <MenuItem value="Aotai">Aotai</MenuItem>
                  <MenuItem value="HS Fashion">HS Fashion</MenuItem>
                  <MenuItem value="NML">NML</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel id="tipo_paqueteria-label">{t("Clientes.CountryProd")}</InputLabel>
                <Select
                  labelId="tipo_paqueteria-label"
                  name="tipo_paqueteria"
                  multiple
                  label={t("Clientes.CountryProd")}
                  value={customer.tipo_paqueteria}
                  onChange={(e) => handleMultiSelectChange(e, "tipo_paqueteria")}
                  renderValue={renderMultiChips}
                >
                  <MenuItem value="Bangladesh">Bangladesh</MenuItem>
                  <MenuItem value="Cambodia">Cambodia</MenuItem>
                  <MenuItem value="China">China</MenuItem>
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Turkia">Turkia</MenuItem>
                  <MenuItem value="Myanmar">Myanmar</MenuItem>
                  <MenuItem value="Vietnam">Vietnam</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSection.company}
          onChange={toggleSection("company")}
          disableGutters
          elevation={0}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography sx={ui.sectionTitleSx}>
              {t("Clientes.companySection", { defaultValue: "Empresa" })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <Box sx={fieldStackSx}>
              <TextField
                size="small"
                name="name"
                label={t("Clientes.CompanyName")}
                value={customer.name}
                onChange={handleCustomerChange}
                fullWidth
              />

              <FormControl fullWidth size="small">
                <InputLabel id="marcas-label">{t("Clientes.Brand")}</InputLabel>
                <Select
                  labelId="marcas-label"
                  name="marcas"
                  multiple
                  label={t("Clientes.Brand")}
                  value={Array.isArray(customer.marcas) ? customer.marcas : []}
                  onChange={(e) => handleMultiSelectChange(e, "marcas")}
                  renderValue={renderMultiChips}
                >
                  {marcas.map((marca) => (
                    <MenuItem key={marca.ID_MARCA} value={marca.MARCA}>
                      {marca.MARCA}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                name="contact"
                label={t("Clientes.Contact")}
                value={customer.contact}
                onChange={handleCustomerChange}
                fullWidth
              />

              <TextField
                size="small"
                name="address"
                label={t("Clientes.Address")}
                value={customer.address}
                onChange={handleCustomerChange}
                fullWidth
              />

              <Box sx={fieldGridSx}>
                <TextField
                  size="small"
                  name="zipCode"
                  label={t("Clientes.PostalCode")}
                  value={customer.zipCode}
                  onChange={handleCustomerChange}
                />
                <TextField
                  size="small"
                  name="city"
                  label={t("Clientes.City")}
                  value={customer.city}
                  onChange={handleCustomerChange}
                />
                <TextField
                  size="small"
                  name="state"
                  label={t("Clientes.Province")}
                  value={customer.state}
                  onChange={handleCustomerChange}
                />
                <TextField
                  size="small"
                  name="country"
                  label={t("Clientes.Country")}
                  value={customer.country}
                  onChange={handleCustomerChange}
                />
                <TextField
                  size="small"
                  name="phone"
                  label={t("Clientes.Phone")}
                  value={customer.phone}
                  onChange={handleCustomerChange}
                />
                <TextField
                  size="small"
                  name="email"
                  label={t("Clientes.Email")}
                  value={customer.email}
                  onChange={handleCustomerChange}
                />
              </Box>

              <TextField
                size="small"
                name="Web"
                label={t("Clientes.website")}
                value={customer.Web}
                onChange={handleCustomerChange}
                fullWidth
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSection.business}
          onChange={toggleSection("business")}
          disableGutters
          elevation={0}
          sx={{ ...accordionSx, mb: 0 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography sx={ui.sectionTitleSx}>
              {t("Clientes.businessSection", { defaultValue: "Negocio" })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={accordionDetailsSx}>
            <Box sx={fieldStackSx}>
              <Box sx={fieldGridSx}>
                <FormControl fullWidth size="small">
                  <InputLabel id="abc-label">{t("Clientes.ABC")}</InputLabel>
                  <Select
                    labelId="abc-label"
                    name="abc"
                    label={t("Clientes.ABC")}
                    value={customer.abc}
                    onChange={handleCustomerChange}
                  >
                    <MenuItem value="AA">AA</MenuItem>
                    <MenuItem value="AB">AB</MenuItem>
                    <MenuItem value="AC">AC</MenuItem>
                    <MenuItem value="BA">BA</MenuItem>
                    <MenuItem value="BB">BB</MenuItem>
                    <MenuItem value="BC">BC</MenuItem>
                    <MenuItem value="CA">CA</MenuItem>
                    <MenuItem value="CB">CB</MenuItem>
                    <MenuItem value="CC">CC</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  name="ingresos_estimados"
                  label={t("Clientes.EstimatedIncome")}
                  value={customer.ingresos_estimados}
                  onChange={handleCustomerChange}
                  fullWidth
                />
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel id="responsableVentas-label">
                  {t("Clientes.AccManager")}
                </InputLabel>
                <Select
                  labelId="responsableVentas-label"
                  name="responsableVentas"
                  label={t("Clientes.AccManager")}
                  value={customer.responsableVentas}
                  onChange={handleCustomerChange}
                >
                  <MenuItem value="Irene">Irene</MenuItem>
                  <MenuItem value="Lydia">Lydia</MenuItem>
                  <MenuItem value="Imma">Imma</MenuItem>
                  <MenuItem value="Sara">Sara</MenuItem>
                  <MenuItem value="Agustina">Agustina</MenuItem>
                  <MenuItem value="Laura">Laura</MenuItem>
                  <MenuItem value="Chiara">Chiara</MenuItem>
                  <MenuItem value="Alex">Alex</MenuItem>
                  <MenuItem value="Borja">Borja</MenuItem>
                  <MenuItem value="Miguel">Miguel</MenuItem>
                  <MenuItem value="Mikael">Mikael</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                name="observaciones"
                label={t("Clientes.observations")}
                value={customer.observaciones}
                onChange={handleCustomerChange}
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default CustomerDetails;
